// Pago a Proveedores · SOLICITAR PAGO (pestaña de trabajo, docs/108 B5). Modos como el menú:
//   factura       — pagar UNA factura (por defecto, lo disponible);
//   documentos    — varios documentos del proveedor, cada uno con su monto;
//   estado-cuenta — un monto repartido del documento más antiguo al más nuevo;
//   masivo        — documentos de VARIOS proveedores: un pago por proveedor, en un lote;
//   anticipo      — pago sin documentos (queda como anticipo al proveedor).
// El pago queda POR AUTORIZAR: lo autoriza otra persona en «Autorizaciones», y recién al registrarlo
// pagado (con comprobante) descuenta la deuda. Lo comprometido en pagos en curso no se vuelve a ofrecer.
// Misma estructura que el cobro a clientes (pedido de Rodrigo, 2026-10-19): tarjetas Proveedor ·
// Medio · Estado de cuenta · Resumen. Diferencias del pago: se eligen la cuenta de la EMPRESA desde la
// que se transfiere (o con la que se gira el cheque) y la cuenta del PROVEEDOR a la que se transfiere
// (su ficha), y se pueden tomar sus NOTAS DE CRÉDITO como descuento: rebajan las facturas marcadas y el
// pago queda solo por lo que falta en dinero.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { entero } from '../comun/medios';
import { fecha, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { BuscadorCliente } from '../ventas/BuscadorCliente';
import {
  clavesPagos,
  siglaCompra,
  useCuentasDestino,
  useCuentasEmpresa,
  useDocumentosCxp,
  useNotasCreditoCxp,
  type DocumentoProveedor,
} from './datosPagos';
import { ICONO, trazo } from './EditorMedios';
import { SelectorBanco } from '../comun/bancos';
import { ImprimirCartolaProveedor, ImprimirPagoPorId } from './Impresos';
import { hoyIso } from '../ventas/comercial';

export type ModoPago = 'factura' | 'documentos' | 'estado-cuenta' | 'masivo' | 'anticipo';
// Como en el cobro (2026-10-16): pagar factura, pago a documento y pago a estado de cuenta son UNA
// pantalla, «Pagar»: varios documentos, uno completo y otro abonado con lo que queda del monto.
const UNIFICADOS = new Set<ModoPago>(['factura', 'documentos', 'estado-cuenta']);
const TITULO: Record<ModoPago, string> = {
  factura: 'Pagar',
  documentos: 'Pagar',
  'estado-cuenta': 'Pago a estado de cuenta',
  masivo: 'Pago masivo',
  anticipo: 'Anticipo a proveedor',
};
type Medio = 'TRANSFERENCIA' | 'CHEQUE' | 'VALE_VISTA' | 'EFECTIVO';
const MEDIOS: Array<[Medio, string]> = [
  ['TRANSFERENCIA', 'Transferencia'],
  ['CHEQUE', 'Cheque'],
  ['VALE_VISTA', 'Vale vista'],
  ['EFECTIVO', 'Efectivo'],
];
const ICONO_MEDIO: Record<Medio, ReactNode> = {
  TRANSFERENCIA: ICONO.TRANSFERENCIA,
  CHEQUE: ICONO.CHEQUE,
  EFECTIVO: ICONO.EFECTIVO,
  VALE_VISTA: (
    <svg {...trazo}>
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M14 3v5h5M9 13h7M9 17h5" />
    </svg>
  ),
};

/** Reparte las NC (en orden) sobre lo marcado de cada factura (en orden): qué NC rebaja qué factura. */
function repartirNc(
  docs: Array<{ documentoId: string; monto: number }>,
  ncs: Array<{ documentoId: string; monto: number }>,
) {
  const imputaciones: Array<{ ncDocumentoId: string; documentoId: string; monto: number }> = [];
  const porDoc = new Map<string, number>();
  let i = 0;
  let libre = ncs[0]?.monto ?? 0;
  for (const d of docs) {
    let falta = d.monto;
    while (falta > 0 && i < ncs.length) {
      if (libre <= 0) {
        i += 1;
        libre = ncs[i]?.monto ?? 0;
        continue;
      }
      const x = Math.min(libre, falta);
      imputaciones.push({
        ncDocumentoId: ncs[i]!.documentoId,
        documentoId: d.documentoId,
        monto: x,
      });
      porDoc.set(d.documentoId, (porDoc.get(d.documentoId) ?? 0) + x);
      libre -= x;
      falta -= x;
    }
  }
  const sobra = libre + ncs.slice(i + 1).reduce((s, n) => s + n.monto, 0);
  return { imputaciones, porDoc, sobra };
}

/** Un medio del pago. Con varios (p. ej. cheques a distintas fechas) cada uno es un pago del lote. */
type LineaMedio = {
  medio: Medio;
  monto: string;
  fecha: string;
  cuentaOrigen: string;
  cuentaDestino: string;
  banco: string;
  numeroCheque: string;
};
const lineaNueva = (medio: Medio = 'TRANSFERENCIA'): LineaMedio => ({
  medio,
  monto: '',
  fecha: hoyIso(),
  cuentaOrigen: '',
  cuentaDestino: '',
  banco: '',
  numeroCheque: '',
});

/** Reparte lo que se paga en dinero por documento (en orden) entre los medios (en orden). */
function repartirMedios(docs: Array<{ documentoId: string; monto: number }>, montos: number[]) {
  const cola = docs.filter((d) => d.monto > 0).map((d) => ({ ...d }));
  return montos.map((total) => {
    const out: Array<{ documentoId: string; monto: string }> = [];
    let falta = total;
    while (falta > 0 && cola.length) {
      const d = cola[0]!;
      const x = Math.min(falta, d.monto);
      out.push({ documentoId: d.documentoId, monto: String(x) });
      d.monto -= x;
      falta -= x;
      if (!d.monto) cola.shift();
    }
    return out;
  });
}

export function PagoProveedor({
  modo: modoPedido,
  proveedorInicial,
}: {
  modo: ModoPago;
  /** Desde la ficha del proveedor: viene elegido. */
  proveedorInicial?: { id: string; razonSocial: string } | undefined;
}) {
  const modo: ModoPago = UNIFICADOS.has(modoPedido) ? 'documentos' : modoPedido;
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const intentos = useIntentos();
  const [proveedor, setProveedor] = useState<{ id: string; razonSocial: string } | null>(
    proveedorInicial ?? null,
  );
  const [buscando, setBuscando] = useState(false);
  const [montos, setMontos] = useState<Record<string, string>>({});
  const [ncUsadas, setNcUsadas] = useState<Record<string, string>>({});
  const [montoCuenta, setMontoCuenta] = useState('');
  const [lineas, setLineas] = useState<LineaMedio[]>([lineaNueva()]);
  const poner = (i: number, c: Partial<LineaMedio>) =>
    setLineas((ls) => ls.map((l, j) => (j === i ? { ...l, ...c } : l)));
  // Con un solo medio se ve el formulario de siempre; con más, la grilla de medios.
  const l0 = lineas[0]!;
  const varios = lineas.length > 1;
  const [observacion, setObservacion] = useState('');
  const [hecho, setHecho] = useState<{
    proveedor: { id: string; razonSocial: string } | null;
    pagos: Array<{ pagoId: string; numero: number }>;
    total: number;
    nc: number;
  } | null>(null);
  const [imprimir, setImprimir] = useState<{ pagoId: string; proveedorId: string } | null>(null);
  const [imprimirCuenta, setImprimirCuenta] = useState(false);
  const terminar = useCallback(() => setImprimir(null), []);
  const terminarCuenta = useCallback(() => setImprimirCuenta(false), []);
  const masivo = modo === 'masivo';
  const docs = useDocumentosCxp(
    empresaId,
    { proveedorEntidadId: masivo ? undefined : proveedor?.id, abiertos: true },
    masivo || (!!proveedor && modo !== 'anticipo'),
  );
  const notas = useNotasCreditoCxp(
    empresaId,
    masivo || modo === 'anticipo' ? undefined : proveedor?.id,
  );
  const ncDisponibles = useMemo(
    () => (notas.data ?? []).filter((n) => Number(n.disponible) > 0),
    [notas.data],
  );
  const cuentas = useCuentasEmpresa(empresaId);
  const activas = useMemo(() => (cuentas.data ?? []).filter((c) => c.activa), [cuentas.data]);
  const origenesDe = useCallback(
    (m: Medio) => activas.filter((c) => m !== 'CHEQUE' || c.tipoCuenta === 'CORRIENTE'),
    [activas],
  );
  const destinos = useCuentasDestino(empresaId, masivo ? undefined : proveedor?.id);
  // La cuenta del proveedor marcada como principal se propone sola; la de origen, si hay una sola.
  useEffect(() => {
    const p = (destinos.data ?? []).find((c) => c.principal) ?? destinos.data?.[0];
    setLineas((ls) => ls.map((l) => ({ ...l, cuentaDestino: p?.id ?? '' })));
  }, [destinos.data]);
  useEffect(() => {
    setLineas((ls) => {
      let cambio = false;
      const n = ls.map((l) => {
        const os = origenesDe(l.medio);
        if (os.some((c) => c.id === l.cuentaOrigen)) return l;
        const v = os.length === 1 ? os[0]!.id : '';
        if (v === l.cuentaOrigen) return l;
        cambio = true;
        return { ...l, cuentaOrigen: v };
      });
      return cambio ? n : ls;
    });
  }, [origenesDe, lineas]);
  const abiertos = useMemo(
    () => (docs.data ?? []).filter((d) => Number(d.disponible) > 0),
    [docs.data],
  );

  // Lo que se propone pagar de un documento: sin lo retenido por reclamo (espera la NC).
  const pagableDe = (d: DocumentoProveedor) => Number(d.pagable ?? d.disponible);
  // Lo marcado por documento (lo que se salda: dinero + NC), elegido a mano o repartido.
  const aplicaciones = useMemo(() => {
    if (modo === 'anticipo') return [];
    if (modo === 'estado-cuenta') {
      let resto = entero(montoCuenta) || 0;
      const out: Array<{ d: DocumentoProveedor; monto: number }> = [];
      for (const d of abiertos) {
        if (resto <= 0) break;
        const m = Math.min(resto, pagableDe(d));
        if (m <= 0) continue;
        out.push({ d, monto: m });
        resto -= m;
      }
      return out;
    }
    return abiertos
      .filter((d) => montos[d.documentoId])
      .map((d) => ({ d, monto: entero(montos[d.documentoId]!) }))
      .filter((x) => x.monto > 0);
  }, [abiertos, montos, montoCuenta, modo]);
  const excede = aplicaciones.some((x) => x.monto > Number(x.d.disponible));
  const deuda = abiertos.reduce((s, d) => s + Number(d.disponible), 0);
  const vencido = abiertos
    .filter((d) => d.diasVencido > 0)
    .reduce((s, d) => s + Number(d.disponible), 0);
  const porDoc = useMemo(
    () => new Map(aplicaciones.map((x) => [x.d.documentoId, x.monto])),
    [aplicaciones],
  );
  const abonoDe = (d: DocumentoProveedor) => porDoc.get(d.documentoId) ?? 0;
  const marcado = aplicaciones.reduce((s, x) => s + x.monto, 0);

  // NC tomadas como descuento: rebajan lo marcado, en orden.
  const ncElegidas = ncDisponibles
    .filter((n) => ncUsadas[n.documentoId] !== undefined)
    .map((n) => ({ documentoId: n.documentoId, monto: entero(ncUsadas[n.documentoId]!) || 0 }))
    .filter((n) => n.monto > 0);
  const ncExcede = ncDisponibles.some(
    (n) => (entero(ncUsadas[n.documentoId] ?? '') || 0) > Number(n.disponible),
  );
  const reparto = repartirNc(
    aplicaciones.map((x) => ({ documentoId: x.d.documentoId, monto: x.monto })),
    ncElegidas,
  );
  const totalNc = ncElegidas.reduce((s, n) => s + n.monto, 0) - reparto.sobra;
  // Lo reclamado al proveedor no se paga en dinero: solo se cubre con su NC.
  const retenidoExcede = aplicaciones.some(
    (x) =>
      Number(x.d.retenido ?? 0) > 0 &&
      x.monto - (reparto.porDoc.get(x.d.documentoId) ?? 0) > pagableDe(x.d),
  );
  const retenidoTotal = abiertos.reduce((s, d) => s + Number(d.retenido ?? 0), 0);
  const montoMedios = lineas.reduce((s, l) => s + (entero(l.monto) || 0), 0);
  const total =
    modo === 'anticipo' ? (varios ? montoMedios : entero(montoCuenta) || 0) : marcado - totalNc;
  // Con varios medios, entre todos suman justo lo que se paga en dinero.
  const faltaMedios = varios ? total - montoMedios : 0;
  const porProveedor = useMemo(() => {
    const m = new Map<string, Array<{ d: DocumentoProveedor; monto: number }>>();
    for (const x of aplicaciones) {
      const k = x.d.proveedorEntidadId ?? '';
      m.set(k, [...(m.get(k) ?? []), x]);
    }
    return m;
  }, [aplicaciones]);
  const sinCuentasDe = (m: Medio) => !origenesDe(m).length;
  const sinCuentas = sinCuentasDe(l0.medio);
  const pideOrigen = l0.medio !== 'EFECTIVO';
  /** Qué le falta a un medio para poder solicitarse (null = completo). */
  const faltaDe = (l: LineaMedio) => {
    if (varios && !(entero(l.monto) > 0)) return 'monto';
    if (l.medio === 'EFECTIVO' || l.cuentaOrigen) return null;
    if (!sinCuentasDe(l.medio)) return l.medio === 'CHEQUE' ? 'cuenta corriente' : 'cuenta';
    return l.medio === 'CHEQUE' && l.banco.trim().length < 2 ? 'banco' : null;
  };
  const valido =
    (total > 0 || totalNc > 0) &&
    !excede &&
    !retenidoExcede &&
    !ncExcede &&
    reparto.sobra === 0 &&
    (masivo ? porProveedor.size > 0 : !!proveedor) &&
    lineas.every((l) => !faltaDe(l)) &&
    faltaMedios === 0;

  // Con «Monto a pagar» escrito (o con varios medios, lo que suman), al marcar un documento se le
  // asigna lo que queda del monto (uno completo y el siguiente abonado); sin monto, lo disponible.
  const tope = varios ? montoMedios : entero(montoCuenta) || 0;
  const alternar = (d: DocumentoProveedor) =>
    setMontos((m) => {
      const n = { ...m };
      if (n[d.documentoId] !== undefined) {
        delete n[d.documentoId];
        return n;
      }
      const usado = Object.values(n).reduce((s, v) => s + (entero(v) || 0), 0);
      const resto = tope - usado;
      n[d.documentoId] = String(tope && resto > 0 ? Math.min(resto, pagableDe(d)) : pagableDe(d));
      return n;
    });
  // «Asignar el monto»: del documento más antiguo al más nuevo.
  const asignar = () => {
    let resto = tope;
    const n: Record<string, string> = {};
    for (const d of abiertos) {
      if (resto <= 0) break;
      const x = Math.min(resto, pagableDe(d));
      if (x <= 0) continue;
      n[d.documentoId] = String(x);
      resto -= x;
    }
    setMontos(n);
  };
  const alternarNc = (id: string, disponible: string) =>
    setNcUsadas((m) => {
      const n = { ...m };
      if (n[id] !== undefined) delete n[id];
      else n[id] = disponible;
      return n;
    });

  const solicitar = useMutation({
    mutationFn: () => {
      const datosDe = (l: LineaMedio) => ({
        medio: l.medio,
        fechaProgramada: l.fecha,
        banco: l.medio !== 'EFECTIVO' && sinCuentasDe(l.medio) ? l.banco.trim() || null : null,
        numeroDocumento: l.medio === 'CHEQUE' ? l.numeroCheque.trim() || null : null,
        observacion: observacion.trim() || null,
        cuentaOrigenId: l.medio !== 'EFECTIVO' && l.cuentaOrigen ? l.cuentaOrigen : null,
        cuentaDestinoId:
          !masivo && l.medio === 'TRANSFERENCIA' && l.cuentaDestino ? l.cuentaDestino : null,
      });
      const base = datosDe(l0);
      const montosMedios = lineas.map((l) => entero(l.monto) || 0);
      const pagos =
        modo === 'anticipo'
          ? varios
            ? lineas.map((l, i) => ({
                proveedorEntidadId: proveedor!.id,
                total: String(montosMedios[i]),
                ...datosDe(l),
                aplicaciones: [],
              }))
            : [
                {
                  proveedorEntidadId: proveedor!.id,
                  total: String(total),
                  ...base,
                  aplicaciones: [],
                },
              ]
          : varios
            ? // Un pago por medio, en el mismo lote: los documentos se reparten en orden entre los
              // medios y las NC van con el primero.
              repartirMedios(
                aplicaciones.map((x) => ({
                  documentoId: x.d.documentoId,
                  monto: x.monto - (reparto.porDoc.get(x.d.documentoId) ?? 0),
                })),
                montosMedios,
              ).map((apl, i) => ({
                proveedorEntidadId: proveedor!.id,
                total: String(montosMedios[i]),
                ...datosDe(lineas[i]!),
                aplicaciones: apl,
                notasCredito:
                  i === 0
                    ? reparto.imputaciones.map((n) => ({
                        ncDocumentoId: n.ncDocumentoId,
                        documentoId: n.documentoId,
                        monto: String(n.monto),
                      }))
                    : [],
              }))
            : [...porProveedor].map(([prov, xs]) => {
                const conNc = masivo ? [] : reparto.imputaciones;
                const efectivo = xs.map((x) => ({
                  documentoId: x.d.documentoId,
                  monto: x.monto - (masivo ? 0 : (reparto.porDoc.get(x.d.documentoId) ?? 0)),
                }));
                return {
                  proveedorEntidadId: prov,
                  total: String(efectivo.reduce((s, x) => s + x.monto, 0)),
                  ...base,
                  aplicaciones: efectivo
                    .filter((x) => x.monto > 0)
                    .map((x) => ({ documentoId: x.documentoId, monto: String(x.monto) })),
                  notasCredito: conNc.map((n) => ({
                    ncDocumentoId: n.ncDocumentoId,
                    documentoId: n.documentoId,
                    monto: String(n.monto),
                  })),
                };
              });
      const cuerpo = { empresaId, pagos };
      return api.post<{ loteId: string; pagos: Array<{ pagoId: string; numero: number }> }>(
        '/tesoreria/pagos',
        cuerpo,
        {
          idempotencia: intentos.clave('pago', cuerpo),
        },
      );
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      setHecho({ proveedor: masivo ? null : proveedor, pagos: r.pagos, total, nc: totalNc });
      await qc.invalidateQueries({ queryKey: clavesPagos.pagos(empresaId) });
    },
    onError: (e) => intentos.cerrar(e),
  });

  // «Nuevo pago»: pantalla limpia, sin el proveedor anterior (como el cobro).
  const nuevo = () => {
    setHecho(null);
    setProveedor(null);
    setMontos({});
    setNcUsadas({});
    setMontoCuenta('');
    setLineas((ls) => [{ ...ls[0]!, monto: '', banco: '', numeroCheque: '', fecha: hoyIso() }]);
    setObservacion('');
  };
  // «+ Otro medio»: el primero se queda con todo lo que se paga y el nuevo copia su medio y cuenta
  // (lo común: varios cheques de la misma cuenta a distintas fechas); «Completar» ajusta el último.
  const otroMedio = () =>
    setLineas((ls) => {
      const ult = ls[ls.length - 1]!;
      const primero =
        ls.length === 1
          ? [
              {
                ...ls[0]!,
                monto: String(modo === 'anticipo' ? montoCuenta : Math.max(0, total) || ''),
              },
            ]
          : ls;
      return [...primero, { ...ult, monto: '', numeroCheque: '' }];
    });
  const completar = () => {
    const otras = lineas.slice(0, -1).reduce((s, l) => s + (entero(l.monto) || 0), 0);
    poner(lineas.length - 1, { monto: String(Math.max(0, total - otras)) });
  };

  if (!puede('tesoreria.pago.solicitar'))
    return (
      <div className="pantalla">
        <p className="aviso-linea tono-aviso">
          Solicitar pagos requiere el permiso «Solicitar pagos a proveedores».
        </p>
      </div>
    );

  const textoOrigen = l0.medio === 'CHEQUE' ? 'Cuenta corriente del cheque' : 'Desde la cuenta';

  return (
    <div className="pantalla cobro-tesoreria" data-pantalla="pago-proveedor" data-modo={modo}>
      <header className="encabezado-entidad">
        <span className="ruta-migas">Pago a Proveedores › {TITULO[modo]}</span>
        <h2>{TITULO[modo]}</h2>
      </header>
      {hecho ? (
        <section className="resultado-cobro" aria-label="Pago solicitado" role="status">
          <h3>
            {hecho.pagos.length === 1
              ? `Pago N° ${hecho.pagos[0]!.numero} solicitado: queda por autorizar.`
              : `${hecho.pagos.length} pagos solicitados (N° ${hecho.pagos.map((p) => p.numero).join(', ')}): quedan por autorizar.`}
          </h3>
          <p>
            A pagar: <b>{pesos(String(hecho.total))}</b>
            {hecho.nc > 0 && (
              <>
                {' '}
                · Descontado con notas de crédito: <b>{pesos(String(hecho.nc))}</b>
              </>
            )}
          </p>
          <div className="acciones-modal">
            {hecho.proveedor &&
              hecho.pagos.map((p) => (
                <button
                  key={p.pagoId}
                  type="button"
                  onClick={() =>
                    setImprimir({ pagoId: p.pagoId, proveedorId: hecho.proveedor!.id })
                  }
                >
                  Imprimir comprobante N° {p.numero}
                </button>
              ))}
            <button type="button" className="primario" autoFocus onClick={nuevo}>
              Nuevo pago
            </button>
          </div>
        </section>
      ) : (
        <>
          <section className="paso-cobro" aria-label="Proveedor">
            <div className="barra-filtros">
              {masivo ? (
                <span>
                  Documentos de <b>todos los proveedores</b>: se arma un pago por proveedor.
                </span>
              ) : (
                <div className="campo">
                  <span className="etiqueta">Proveedor</span>
                  <button
                    type="button"
                    className="selector-cliente"
                    aria-label="Elegir proveedor"
                    autoFocus={!proveedor}
                    onClick={() => setBuscando(true)}
                  >
                    {proveedor ? proveedor.razonSocial : 'Buscar proveedor…'}
                  </button>
                </div>
              )}
              <span className="espaciador" />
              {(masivo || proveedor) && modo !== 'anticipo' && (
                <div className="cifras-cliente">
                  <span className="cifra">
                    <span className="etiqueta">Documentos</span>
                    <b>{abiertos.length}</b>
                  </span>
                  <span className="cifra">
                    <span className="etiqueta">Deuda disponible</span>
                    <b>{pesos(String(deuda))}</b>
                  </span>
                  <span className={`cifra ${vencido > 0 ? 'mala' : ''}`}>
                    <span className="etiqueta">Vencido</span>
                    <b>{pesos(String(vencido))}</b>
                  </span>
                  {ncDisponibles.length > 0 && (
                    <span className="cifra buena">
                      <span className="etiqueta">NC a favor</span>
                      <b>
                        {pesos(String(ncDisponibles.reduce((s, n) => s + Number(n.disponible), 0)))}
                      </b>
                    </span>
                  )}
                </div>
              )}
              {proveedor && (
                <button type="button" onClick={() => setImprimirCuenta(true)}>
                  Imprimir estado de cuenta
                </button>
              )}
            </div>
          </section>
          {!masivo && !proveedor && (
            <p className="apagado vacio-centrado">
              Elige el proveedor para ver su deuda, sus notas de crédito y registrar el pago.
            </p>
          )}

          {(masivo || proveedor) && (
            <section aria-label="Datos del pago" className="paso-cobro">
              {!varios && (
                <div className="editor-medios">
                  <div className="botones-medio" role="group" aria-label="Medio de pago">
                    {MEDIOS.map(([k, n]) => (
                      <button
                        key={k}
                        type="button"
                        className={l0.medio === k ? 'activo' : ''}
                        aria-pressed={l0.medio === k}
                        onClick={() => poner(0, { medio: k })}
                      >
                        {ICONO_MEDIO[k]}
                        <span>{n}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {varios && (
                <table className="grilla compacta cobro-medios" aria-label="Medios de pago">
                  <thead>
                    <tr>
                      <th className="der">Lín</th>
                      <th>Medio</th>
                      <th>Desde la cuenta / banco</th>
                      <th>A la cuenta del proveedor</th>
                      <th>N° cheque</th>
                      <th>Fecha</th>
                      <th className="der">Monto</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {lineas.map((l, i) => {
                      const falta = faltaDe(l);
                      const sc = sinCuentasDe(l.medio);
                      return (
                        <tr key={i} data-medio={l.medio} className={falta ? 'con-problema' : ''}>
                          <td className="der mono">{i + 1}</td>
                          <td>
                            <select
                              aria-label={`Medio ${i + 1}`}
                              value={l.medio}
                              onChange={(e) => poner(i, { medio: e.target.value as Medio })}
                            >
                              {MEDIOS.map(([k, n]) => (
                                <option key={k} value={k}>
                                  {n}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            {l.medio === 'EFECTIVO' ? (
                              <span className="apagado">—</span>
                            ) : sc ? (
                              <SelectorBanco
                                etiqueta={`Banco ${i + 1}`}
                                valor={l.banco}
                                alCambiar={(v) => poner(i, { banco: v })}
                              />
                            ) : (
                              <select
                                aria-label={`Cuenta de origen ${i + 1}`}
                                value={l.cuentaOrigen}
                                onChange={(e) => poner(i, { cuentaOrigen: e.target.value })}
                              >
                                <option value="">Elegir cuenta…</option>
                                {origenesDe(l.medio).map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.texto}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            {l.medio === 'TRANSFERENCIA' ? (
                              <select
                                aria-label={`Cuenta de destino ${i + 1}`}
                                value={l.cuentaDestino}
                                onChange={(e) => poner(i, { cuentaDestino: e.target.value })}
                              >
                                <option value="">
                                  {destinos.data?.length
                                    ? 'Sin indicar'
                                    : 'Sin cuentas en su ficha'}
                                </option>
                                {(destinos.data ?? []).map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.texto}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="apagado">—</span>
                            )}
                          </td>
                          <td>
                            {l.medio === 'CHEQUE' ? (
                              <input
                                aria-label={`N° cheque ${i + 1}`}
                                inputMode="numeric"
                                placeholder="N° de cheque"
                                value={l.numeroCheque}
                                onChange={(e) => poner(i, { numeroCheque: e.target.value })}
                              />
                            ) : (
                              <span className="apagado">—</span>
                            )}
                          </td>
                          <td>
                            <input
                              type="date"
                              aria-label={`Fecha ${i + 1}`}
                              title={l.medio === 'CHEQUE' ? 'Fecha del cheque' : 'Fecha de pago'}
                              value={l.fecha}
                              onChange={(e) => poner(i, { fecha: e.target.value })}
                            />
                          </td>
                          <td className="der">
                            <input
                              aria-label={`Monto ${i + 1}`}
                              className="celda-num fuerte"
                              inputMode="numeric"
                              placeholder="$ 0"
                              value={l.monto}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => poner(i, { monto: e.target.value })}
                            />
                            {falta && falta !== 'monto' && (
                              <div className="cobro-falta">falta: {falta}</div>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="boton-icono"
                              aria-label={`Quitar medio ${i + 1}`}
                              onClick={() => setLineas((ls) => ls.filter((_, j) => j !== i))}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>
                        <button type="button" disabled={lineas.length >= 12} onClick={otroMedio}>
                          + Otro medio
                        </button>
                      </td>
                      <td colSpan={3} className="der">
                        {modo !== 'anticipo' && total > 0 && faltaMedios !== 0 && (
                          <button
                            type="button"
                            title="Deja el último medio justo en lo que falta"
                            onClick={completar}
                          >
                            Completar con lo marcado ({pesos(String(total))})
                          </button>
                        )}{' '}
                        <b>Total medios</b>
                      </td>
                      <td className="der total-medios">
                        <b>{pesos(String(montoMedios))}</b>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              )}
              <div className="barra-filtros datos-pago">
                {!varios && pideOrigen && !sinCuentas && (
                  <label className="campo">
                    <span className="etiqueta">{textoOrigen}</span>
                    <select
                      aria-label="Cuenta de origen"
                      value={l0.cuentaOrigen}
                      onChange={(e) => poner(0, { cuentaOrigen: e.target.value })}
                    >
                      <option value="">Elegir cuenta…</option>
                      {origenesDe(l0.medio).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.texto}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {!varios && pideOrigen && sinCuentas && (
                  <label className="campo">
                    <span className="etiqueta">Banco</span>
                    <SelectorBanco
                      etiqueta="Banco"
                      valor={l0.banco}
                      alCambiar={(v) => poner(0, { banco: v })}
                    />
                  </label>
                )}
                {!varios && l0.medio === 'TRANSFERENCIA' && !masivo && (
                  <label className="campo">
                    <span className="etiqueta">A la cuenta del proveedor</span>
                    <select
                      aria-label="Cuenta de destino"
                      value={l0.cuentaDestino}
                      onChange={(e) => poner(0, { cuentaDestino: e.target.value })}
                    >
                      <option value="">
                        {destinos.data?.length ? 'Sin indicar' : 'Sin cuentas en su ficha'}
                      </option>
                      {(destinos.data ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.texto}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {!varios && l0.medio === 'CHEQUE' && (
                  <label className="campo campo-corto">
                    <span className="etiqueta">N° de cheque</span>
                    <input
                      aria-label="Número de cheque"
                      inputMode="numeric"
                      value={l0.numeroCheque}
                      onChange={(e) => poner(0, { numeroCheque: e.target.value })}
                    />
                  </label>
                )}
                {!varios && (
                  <label className="campo">
                    <span className="etiqueta">
                      {l0.medio === 'CHEQUE' ? 'Fecha del cheque' : 'Fecha de pago'}
                    </span>
                    <input
                      type="date"
                      aria-label="Fecha programada"
                      value={l0.fecha}
                      onChange={(e) => poner(0, { fecha: e.target.value })}
                    />
                  </label>
                )}
                {!varios && (modo === 'documentos' || modo === 'anticipo') && (
                  <label className="campo campo-corto">
                    <span className="etiqueta">
                      {modo === 'anticipo' ? 'Monto del anticipo' : 'Monto a pagar (opcional)'}
                    </span>
                    <input
                      aria-label="Monto a pagar"
                      inputMode="numeric"
                      value={montoCuenta}
                      onChange={(e) => setMontoCuenta(e.target.value)}
                    />
                  </label>
                )}
                <label className="campo campo-ancho">
                  <span className="etiqueta">Observación</span>
                  <input
                    aria-label="Observación del pago"
                    value={observacion}
                    onChange={(e) => setObservacion(e.target.value)}
                  />
                </label>
                {!varios && !masivo && (
                  <button
                    type="button"
                    className="boton-otro-medio"
                    title="Pagar con más de un medio: varios cheques a distintas fechas, parte transferencia y parte cheque…"
                    onClick={otroMedio}
                  >
                    + Otro medio
                  </button>
                )}
              </div>
              {lineas.some((l) => l.medio !== 'EFECTIVO' && sinCuentasDe(l.medio)) && (
                <p className="apagado">
                  No hay cuentas bancarias de la empresa registradas: regístralas en Pago a
                  Proveedores › Cuentas bancarias para elegir desde cuál se paga.
                </p>
              )}
              {varios && faltaMedios !== 0 && (
                <p className="tono-malo">
                  {faltaMedios > 0
                    ? `Faltan ${pesos(String(faltaMedios))} por asignar a un medio.`
                    : `Los medios suman ${pesos(String(-faltaMedios))} más de lo que se paga.`}
                </p>
              )}
            </section>
          )}

          {(masivo || (proveedor && modo !== 'anticipo')) && (
            <section aria-label="Estado de cuenta" className="paso-cobro">
              <div className="barra-filtros">
                <span className="espaciador" />
                {(modo === 'documentos' || masivo) && (
                  <>
                    {modo === 'documentos' && (
                      <button
                        type="button"
                        disabled={!tope}
                        title="Reparte el monto a pagar del documento más antiguo al más nuevo"
                        onClick={asignar}
                      >
                        Asignar el monto
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={!abiertos.length}
                      onClick={() =>
                        setMontos(
                          Object.fromEntries(abiertos.map((d) => [d.documentoId, d.disponible])),
                        )
                      }
                    >
                      Marcar todos
                    </button>
                    <button
                      type="button"
                      disabled={!Object.keys(montos).length}
                      onClick={() => setMontos({})}
                    >
                      Limpiar abonos
                    </button>
                  </>
                )}
              </div>
              {modo === 'estado-cuenta' && (
                <p className="apagado">El monto se abona del documento más antiguo al más nuevo.</p>
              )}
              <table className="grilla compacta grilla-cuenta" aria-label="Documentos por pagar">
                <thead>
                  <tr>
                    {modo !== 'estado-cuenta' && <th />}
                    {masivo && <th>Proveedor</th>}
                    <th>TD</th>
                    <th>Número</th>
                    <th>F. Emisión</th>
                    <th>F. Venc.</th>
                    <th className="der">Días</th>
                    <th className="der">Valor doc.</th>
                    <th className="der">NC</th>
                    <th className="der">Pagado</th>
                    <th className="der">En pagos en curso</th>
                    <th className="der">En reclamo (espera NC)</th>
                    <th className="der">Saldo ant.</th>
                    <th className="der columna-abono">Abono</th>
                    <th className="der">Saldo act.</th>
                  </tr>
                </thead>
                <tbody>
                  {abiertos.map((d) => {
                    const abono = abonoDe(d);
                    const conNc = reparto.porDoc.get(d.documentoId) ?? 0;
                    const retenido = Number(d.retenido ?? 0);
                    const mal =
                      abono > Number(d.disponible) ||
                      (retenido > 0 && abono - conNc > pagableDe(d));
                    return (
                      <tr
                        key={d.documentoId}
                        className={`${d.diasVencido > 0 ? 'vencido' : ''} ${abono > 0 ? 'con-abono' : ''}`}
                      >
                        {modo !== 'estado-cuenta' && (
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`Pagar documento ${d.folio ?? ''}`}
                              checked={montos[d.documentoId] !== undefined}
                              onChange={() => alternar(d)}
                            />
                          </td>
                        )}
                        {masivo && <td>{d.proveedor}</td>}
                        <td>{siglaCompra(d)}</td>
                        <td className="mono">{d.folio ?? '—'}</td>
                        <td>{fecha(d.fechaEmision)}</td>
                        <td>{fecha(d.fechaVencimiento)}</td>
                        <td className={`der ${d.diasVencido > 0 ? 'tono-malo' : ''}`}>
                          {d.diasVencido || ''}
                        </td>
                        <td className="der">{pesos(d.montoTotal)}</td>
                        <td className="der">
                          {Number(d.notasCredito) ? pesos(d.notasCredito) : ''}
                        </td>
                        <td className="der">{Number(d.pagado) ? pesos(d.pagado) : ''}</td>
                        <td className="der">
                          {Number(d.comprometido) ? pesos(d.comprometido) : ''}
                        </td>
                        <td
                          className={`der ${retenido > 0 ? 'tono-aviso' : ''}`}
                          title={
                            retenido > 0
                              ? (d.reclamos ?? [])
                                  .map(
                                    (r) =>
                                      `${r.descripcion ?? r.tipo}: ${r.motivo ?? ''} (neto ${pesos(r.neto)})`,
                                  )
                                  .join(' · ')
                              : undefined
                          }
                        >
                          {retenido > 0 ? pesos(String(retenido)) : ''}
                        </td>
                        <td className="der">{pesos(d.disponible)}</td>
                        <td className="der columna-abono">
                          {modo === 'estado-cuenta' ? (
                            abono ? (
                              <b>{pesos(String(abono))}</b>
                            ) : (
                              ''
                            )
                          ) : (
                            montos[d.documentoId] !== undefined && (
                              <input
                                className={`celda-num ${mal ? 'con-error' : ''}`}
                                aria-label={`Monto a pagar ${d.folio ?? ''}`}
                                inputMode="numeric"
                                value={montos[d.documentoId]}
                                onChange={(e) =>
                                  setMontos((m) => ({ ...m, [d.documentoId]: e.target.value }))
                                }
                              />
                            )
                          )}
                          {conNc > 0 && (
                            <span
                              className="pagado-con"
                              title="Parte descontada con nota de crédito"
                            >
                              <small>NC {pesos(String(conNc))}</small>
                            </span>
                          )}
                        </td>
                        <td className={`der ${mal ? 'tono-malo' : ''}`}>
                          <b>{pesos(String(Number(d.disponible) - abono))}</b>
                        </td>
                      </tr>
                    );
                  })}
                  {docs.isSuccess && !abiertos.length && (
                    <tr>
                      <td colSpan={15} className="apagado">
                        No hay documentos disponibles para pagar
                      </td>
                    </tr>
                  )}
                </tbody>
                {abiertos.length > 1 && (
                  <tfoot>
                    <tr>
                      <td colSpan={(modo !== 'estado-cuenta' ? 6 : 5) + (masivo ? 1 : 0)}>
                        <b>Totales</b> · {abiertos.length} documentos
                      </td>
                      <td className="der">
                        {pesos(String(abiertos.reduce((s, d) => s + Number(d.montoTotal), 0)))}
                      </td>
                      <td />
                      <td />
                      <td />
                      <td className="der">{retenidoTotal ? pesos(String(retenidoTotal)) : ''}</td>
                      <td className="der">{pesos(String(deuda))}</td>
                      <td className="der columna-abono">
                        <b>{pesos(String(marcado))}</b>
                      </td>
                      <td className="der">
                        <b>{pesos(String(deuda - marcado))}</b>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>

              {!masivo && ncDisponibles.length > 0 && (
                <div className="nc-proveedor">
                  <h4>Notas de crédito del proveedor (descuento)</h4>
                  <table className="grilla compacta grilla-cuenta" aria-label="Notas de crédito">
                    <thead>
                      <tr>
                        <th />
                        <th>TD</th>
                        <th>Número</th>
                        <th>F. Emisión</th>
                        <th className="der">Valor NC</th>
                        <th className="der">Usado</th>
                        <th className="der">Disponible</th>
                        <th className="der columna-abono">Descontar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ncDisponibles.map((n) => {
                        const v = ncUsadas[n.documentoId];
                        const mal = (entero(v ?? '') || 0) > Number(n.disponible);
                        return (
                          <tr key={n.documentoId} className={v !== undefined ? 'con-abono' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Usar NC ${n.folio ?? ''}`}
                                checked={v !== undefined}
                                onChange={() => alternarNc(n.documentoId, n.disponible)}
                              />
                            </td>
                            <td>NCC</td>
                            <td className="mono">{n.folio ?? '—'}</td>
                            <td>{fecha(n.fechaEmision)}</td>
                            <td className="der">{pesos(n.montoTotal)}</td>
                            <td className="der">
                              {Number(n.usado) + Number(n.comprometido)
                                ? pesos(String(Number(n.usado) + Number(n.comprometido)))
                                : ''}
                            </td>
                            <td className="der">{pesos(n.disponible)}</td>
                            <td className="der columna-abono">
                              {v !== undefined && (
                                <input
                                  className={`celda-num ${mal ? 'con-error' : ''}`}
                                  aria-label={`Descontar NC ${n.folio ?? ''}`}
                                  inputMode="numeric"
                                  value={v}
                                  onChange={(e) =>
                                    setNcUsadas((m) => ({ ...m, [n.documentoId]: e.target.value }))
                                  }
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p className="apagado">
                    La NC rebaja las facturas marcadas (de la primera a la última); el pago queda
                    solo por lo que falta en dinero.
                  </p>
                </div>
              )}
            </section>
          )}

          {(masivo || proveedor) && (
            <section className="paso-cobro resumen-cobro" aria-label="Resumen del pago">
              <div className="flujo-cobro">
                {modo !== 'anticipo' && (
                  <>
                    <span className="caja-cifra">
                      <span className="etiqueta">Documentos marcados</span>
                      <b>{pesos(String(marcado))}</b>
                    </span>
                    {totalNc > 0 && (
                      <>
                        <span className="flecha" aria-hidden>
                          −
                        </span>
                        <span className="caja-cifra">
                          <span className="etiqueta">Notas de crédito</span>
                          <b>{pesos(String(totalNc))}</b>
                        </span>
                      </>
                    )}
                    <span className="flecha" aria-hidden>
                      =
                    </span>
                  </>
                )}
                <span className="caja-cifra">
                  <span className="etiqueta">
                    A pagar{masivo && porProveedor.size > 1 ? ` (${porProveedor.size} pagos)` : ''}
                  </span>
                  <b>{pesos(String(Math.max(0, total)))}</b>
                </span>
                <span className="espaciador" />
                <button
                  type="button"
                  className="primario boton-grande"
                  disabled={!valido || solicitar.isPending}
                  onClick={() => solicitar.mutate()}
                >
                  Solicitar pago
                </button>
              </div>
              {excede && <p className="tono-malo">Un monto supera lo disponible del documento.</p>}
              {retenidoExcede && (
                <p className="tono-malo">
                  Una factura tiene un monto en reclamo esperando la nota de crédito del proveedor:
                  esa parte no se paga en dinero (se cubre con la NC cuando llegue).
                </p>
              )}
              {ncExcede && <p className="tono-malo">Una NC supera lo que tiene disponible.</p>}
              {reparto.sobra > 0 && (
                <p className="tono-malo">
                  Las notas de crédito superan lo marcado: marca más facturas o baja el descuento.
                </p>
              )}
              {!varios &&
                pideOrigen &&
                !sinCuentas &&
                !l0.cuentaOrigen &&
                (masivo || proveedor) && (
                  <p className="apagado">Elige la cuenta de la empresa desde la que se paga.</p>
                )}
            </section>
          )}
          {solicitar.isError && <PanelError error={solicitar.error} />}
        </>
      )}
      {imprimir && (
        <ImprimirPagoPorId
          pagoId={imprimir.pagoId}
          proveedorId={imprimir.proveedorId}
          nombre={hecho?.proveedor?.razonSocial ?? ''}
          alTerminar={terminar}
        />
      )}
      {imprimirCuenta && proveedor && (
        <ImprimirCartolaProveedor
          proveedorId={proveedor.id}
          nombre={proveedor.razonSocial}
          alTerminar={terminarCuenta}
        />
      )}
      {buscando && (
        <BuscadorCliente
          empresaId={empresaId}
          rol="proveedor"
          alElegir={(c) => {
            setProveedor({ id: c.id, razonSocial: c.razon_social });
            setBuscando(false);
            setMontos({});
            setNcUsadas({});
          }}
          alCerrar={() => setBuscando(false)}
        />
      )}
    </div>
  );
}
